import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AUTH } from "../api/auth";

const AccountDeletionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [status, setStatus] = useState<{
    type: "idle" | "success" | "error";
    message: string;
  }>({ type: "idle", message: "" });

  // Pre-fill email from URL query params or logged-in user
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
      setConfirmEmail(emailParam);
    } else {
      // Try to get email from logged-in user
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user?.email) {
            setEmail(user.email);
            setConfirmEmail(user.email);
          }
        }
      } catch (error) {
        // Ignore errors
      }
    }
  }, [searchParams]);

  const isEmailValid = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const emailsMatch = email.trim().toLowerCase() === confirmEmail.trim().toLowerCase();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus({ type: "idle", message: "" });

    // Validation
    if (!email.trim() || !isEmailValid(email)) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    if (!confirmEmail.trim()) {
      setStatus({
        type: "error",
        message: "Please confirm your email address.",
      });
      return;
    }

    if (!emailsMatch) {
      setStatus({
        type: "error",
        message: "Email addresses do not match. Please check and try again.",
      });
      return;
    }

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmDeletion = async () => {
    setIsSubmitting(true);
    setStatus({ type: "idle", message: "" });

    try {
      await AUTH.deleteAccount({ email: email.trim() });
      
      // Success - clear user data and redirect
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("expiresIn");
      localStorage.removeItem("loginTime");
      window.dispatchEvent(new Event("localStorageChange"));

      setShowConfirmModal(false);
      setStatus({
        type: "success",
        message: "Your account deletion request has been processed successfully.",
      });

      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error: any) {
      // API always returns success, but handle network errors
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Network error. Please check your connection and try again.";
      
      setStatus({
        type: "error",
        message: errorMessage,
      });
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 16px 40px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background:
            "linear-gradient(145deg, #4E1C81 0%, #AB68C5 100%)",
          borderRadius: 24,
          padding: 32,
          boxShadow: "0 30px 80px rgba(15,23,42,0.8)",
          border: "1px solid rgba(171,104,197,0.7)",
          color: "#e5e7eb",
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{
            border: "none",
            background: "rgba(15,23,42,0.5)",
            color: "rgba(226,232,240,0.9)",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 20,
            cursor: "pointer",
            padding: "6px 10px",
            borderRadius: 999,
          }}
        >
          <span style={{ fontSize: 16 }}>←</span> Back to login
        </button>

        <h1
          style={{
            fontSize: 28,
            fontWeight: 800,
            marginBottom: 8,
            color: "#eef2ff",
          }}
        >
          Delete Account
        </h1>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.7,
            color: "rgba(199,210,254,0.95)",
            marginBottom: 20,
          }}
        >
          This action cannot be undone. All your data, including bookings, engagements, and profile information, will be permanently deleted.
        </p>

        {status.type === "success" && (
          <div
            style={{
              background: "rgba(22,163,74,0.18)",
              color: "#bbf7d0",
              borderRadius: 12,
              padding: "10px 14px",
              border: "1px solid rgba(34,197,94,0.55)",
              fontSize: 13,
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            {status.message}
          </div>
        )}

        {status.type === "error" && (
          <div
            style={{
              background: "rgba(248,113,113,0.16)",
              color: "#fecaca",
              borderRadius: 12,
              padding: "10px 14px",
              border: "1px solid rgba(248,113,113,0.7)",
              fontSize: 13,
              marginBottom: 16,
              fontWeight: 500,
            }}
          >
            {status.message}
          </div>
        )}

        {status.type !== "success" && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  color: "#c7d2fe",
                }}
              >
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={isSubmitting}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: "1px solid rgba(129,140,248,0.9)",
                  background: "rgba(15,23,42,0.9)",
                  color: "#e5e7eb",
                  fontSize: 14,
                  outline: "none",
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  color: "#c7d2fe",
                }}
              >
                Confirm email address
              </label>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={isSubmitting}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: emailsMatch && confirmEmail
                    ? "1px solid rgba(34,197,94,0.7)"
                    : "1px solid rgba(129,140,248,0.9)",
                  background: "rgba(15,23,42,0.9)",
                  color: "#e5e7eb",
                  fontSize: 14,
                  outline: "none",
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              />
              {confirmEmail && !emailsMatch && (
                <p
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: "#fecaca",
                  }}
                >
                  Email addresses do not match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={
                isSubmitting ||
                !email.trim() ||
                !confirmEmail.trim() ||
                !emailsMatch ||
                !isEmailValid(email)
              }
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                fontSize: 15,
                fontWeight: 700,
                color: "#f9fafb",
                background:
                  isSubmitting ||
                  !email.trim() ||
                  !confirmEmail.trim() ||
                  !emailsMatch ||
                  !isEmailValid(email)
                    ? "rgba(171,104,197,0.5)"
                    : "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                cursor:
                  isSubmitting ||
                  !email.trim() ||
                  !confirmEmail.trim() ||
                  !emailsMatch ||
                  !isEmailValid(email)
                    ? "not-allowed"
                    : "pointer",
                boxShadow:
                  isSubmitting ||
                  !email.trim() ||
                  !confirmEmail.trim() ||
                  !emailsMatch ||
                  !isEmailValid(email)
                    ? "none"
                    : "0 18px 40px rgba(220,38,38,0.4)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              {isSubmitting ? "Processing..." : "Request Account Deletion"}
            </button>

            <p
              style={{
                marginTop: 14,
                fontSize: 12,
                color: "rgba(191,219,254,0.9)",
                lineHeight: 1.6,
              }}
            >
              By proceeding, you acknowledge that this action is permanent and cannot be reversed.
            </p>
          </form>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.7)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
            }}
            onClick={() => !isSubmitting && setShowConfirmModal(false)}
          >
            <div
              style={{
                background:
                  "linear-gradient(145deg, #4E1C81 0%, #AB68C5 100%)",
                borderRadius: 20,
                padding: 32,
                maxWidth: 480,
                width: "100%",
                boxShadow: "0 30px 80px rgba(15,23,42,0.9)",
                border: "1px solid rgba(171,104,197,0.7)",
                color: "#e5e7eb",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  marginBottom: 12,
                  color: "#fee2e2",
                }}
              >
                ⚠️ Confirm Account Deletion
              </h2>
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: "rgba(199,210,254,0.95)",
                  marginBottom: 24,
                }}
              >
                Are you absolutely sure you want to delete your account? This action is{" "}
                <strong style={{ color: "#fee2e2" }}>permanent and irreversible</strong>.
                All your data will be permanently deleted, including:
              </p>
              <ul
                style={{
                  fontSize: 13,
                  lineHeight: 1.8,
                  color: "rgba(199,210,254,0.95)",
                  marginBottom: 24,
                  paddingLeft: 20,
                }}
              >
                <li>Your profile and account information</li>
                <li>All bookings and engagements</li>
                <li>Your support cases and messages</li>
                <li>All associated data and history</li>
              </ul>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexDirection: "column",
                }}
              >
                <button
                  type="button"
                  onClick={handleConfirmDeletion}
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 12,
                    border: "none",
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#f9fafb",
                    background: isSubmitting
                      ? "rgba(220,38,38,0.5)"
                      : "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    boxShadow: isSubmitting
                      ? "none"
                      : "0 18px 40px rgba(220,38,38,0.4)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                >
                  {isSubmitting ? "Deleting Account..." : "Yes, Delete My Account"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 12,
                    border: "1px solid rgba(129,140,248,0.9)",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#c7d2fe",
                    background: "rgba(15,23,42,0.5)",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDeletionPage;

